import { supabase } from './supabaseClient';
import { Contact, SafetyStatus, LatLng } from '../types';
import { generateNearbyCoordinates } from './locationService';

interface DBProfile {
    id: string;
    full_name: string;
    status: string;
    location_lat: number;
    location_lng: number;
    location_name: string;
    updated_at: string;
    avatar_url?: string;
    username?: string;
    password_hash?: string;
    reports_count?: number;
    lives_helped?: number;
    location_sharing?: boolean;
}

export const contactService = {

    // ===========================
    // PROFILE MANAGEMENT
    // ===========================

    async ensureProfile(id: string, name: string): Promise<Contact | null> {
        const { data } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', id)
            .single();

        if (data) return mapDBToContact(data);

        const { data: newProfile, error: createError } = await supabase
            .from('profiles')
            .insert([{ id, full_name: name, status: 'SAFE', location_lat: 0, location_lng: 0, location_name: 'Unknown Sector' }])
            .select()
            .single();

        if (createError) return null;
        return mapDBToContact(newProfile);
    },

    async getProfile(userId: string): Promise<Contact | null> {
        const { data, error } = await supabase.from('profiles').select('*').eq('id', userId).single();
        if (error || !data) return null;
        return mapDBToContact(data);
    },

    async updateStatus(id: string, lat: number, lng: number, status: SafetyStatus, locationName?: string) {
        await supabase.from('profiles').update({
            location_lat: lat, location_lng: lng, status, location_name: locationName || 'Unknown', updated_at: new Date().toISOString()
        }).eq('id', id);
    },

    async updateProfileSettings(userId: string, settings: { location_sharing?: boolean; full_name?: string; avatar_url?: string }): Promise<boolean> {
        const { error } = await supabase.from('profiles').update(settings).eq('id', userId);
        return !error;
    },

    // ===========================
    // CONTACT REQUESTS
    // ===========================

    async sendContactRequest(myId: string, contactId: string): Promise<{ success: boolean; error?: string }> {
        if (myId === contactId) return { success: false, error: "Cannot add yourself" };

        const { data: existingContact } = await supabase.from('contacts').select('id').eq('user_id', myId).eq('contact_id', contactId).single();
        if (existingContact) return { success: false, error: "Already connected" };

        const { data: existingRequest } = await supabase.from('contact_requests').select('id, status').eq('sender_id', myId).eq('receiver_id', contactId).single();
        if (existingRequest) return { success: false, error: existingRequest.status === 'pending' ? "Request already sent" : "Already processed" };

        const { data: targetUser } = await supabase.from('profiles').select('id').eq('id', contactId).single();
        if (!targetUser) return { success: false, error: "User not found" };

        const { error } = await supabase.from('contact_requests').insert([{ sender_id: myId, receiver_id: contactId, status: 'pending' }]);
        if (error) return { success: false, error: error.message };
        return { success: true };
    },

    async getPendingContactRequests(myId: string): Promise<{ id: string; senderId: string; senderName: string; senderAvatar: string; createdAt: string }[]> {
        const { data, error } = await supabase
            .from('contact_requests')
            .select('id, sender_id, created_at, profiles!contact_requests_sender_id_fkey(full_name, avatar_url)')
            .eq('receiver_id', myId)
            .eq('status', 'pending')
            .order('created_at', { ascending: false });

        if (error || !data) return [];
        return data.map((req: any) => ({
            id: req.id, senderId: req.sender_id, senderName: req.profiles?.full_name || 'Unknown',
            senderAvatar: req.profiles?.avatar_url || '', createdAt: req.created_at
        }));
    },

    async acceptContactRequest(requestId: string, myId: string, senderId: string): Promise<boolean> {
        const { error: updateError } = await supabase.from('contact_requests').update({ status: 'accepted' }).eq('id', requestId);
        if (updateError) return false;
        await supabase.from('contacts').insert([{ user_id: myId, contact_id: senderId }, { user_id: senderId, contact_id: myId }]);
        return true;
    },

    async declineContactRequest(requestId: string): Promise<boolean> {
        const { error } = await supabase.from('contact_requests').update({ status: 'declined' }).eq('id', requestId);
        return !error;
    },

    subscribeToContactRequests(myId: string, callback: (payload: any) => void) {
        return supabase.channel(`requests:${myId}`)
            .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'contact_requests', filter: `receiver_id=eq.${myId}` }, callback)
            .subscribe();
    },

    // ===========================
    // CONTACTS (CONFIRMED)
    // ===========================

    async getContacts(myId: string): Promise<Contact[]> {
        // 1. Get confirmed contacts from 'contacts' table
        const { data: connections } = await supabase.from('contacts').select('contact_id').eq('user_id', myId);

        // 2. Get accepted requests where I am sender or receiver (Fallback for RLS blocking bidirectional insert)
        const { data: requests } = await supabase.from('contact_requests')
            .select('sender_id, receiver_id')
            .eq('status', 'accepted')
            .or(`sender_id.eq.${myId},receiver_id.eq.${myId}`);

        // 3. Compile Set of Contact IDs
        const contactIds = new Set<string>();

        connections?.forEach((c: any) => contactIds.add(c.contact_id));
        requests?.forEach((r: any) => {
            if (r.sender_id !== myId) contactIds.add(r.sender_id);
            if (r.receiver_id !== myId) contactIds.add(r.receiver_id);
        });

        if (contactIds.size === 0) {
            console.warn(`[getContacts] No contacts found for ${myId} (Connections: ${connections?.length}, Requests: ${requests?.length})`);
            return [];
        }

        console.log(`[getContacts] Fetching profiles for IDs:`, Array.from(contactIds));

        // 4. Fetch profiles
        const { data: profiles, error: profileError } = await supabase
            .from('profiles')
            .select('*')
            .in('id', Array.from(contactIds));

        if (profileError) {
            console.error('[getContacts] Profile Query Error:', profileError);
            return [];
        }

        let fetchedProfiles = profiles || [];

        // CHECK FOR MISSING PROFILES (Ghost / Invisible Users)
        // If RLS blocks profile access, we still want to show the contact exists
        const fetchedIds = new Set(fetchedProfiles.map(p => p.id));
        const missingIds = Array.from(contactIds).filter(id => !fetchedIds.has(id));

        if (missingIds.length > 0) {
            console.warn('[getContacts] Found missing profiles for IDs:', missingIds);
            const ghosts = missingIds.map(id => ({
                id,
                full_name: 'Unknown Agent',
                status: 'UNKNOWN',
                location_lat: 0,
                location_lng: 0,
                location_name: 'Classified',
                updated_at: new Date().toISOString(),
                avatar_url: `https://api.dicebear.com/9.x/avataaars/svg?seed=${id}`
            }));
            fetchedProfiles = [...fetchedProfiles, ...ghosts];
        }

        if (fetchedProfiles.length === 0) {
            console.warn('[getContacts] Profiles returned empty despite valid IDs! RLS?');
            return [];
        }

        return fetchedProfiles.map(mapDBToContact);
    },

    // NEW: Subscribe to contacts changes (for real-time sync when request is accepted)
    subscribeToContacts(myId: string, callback: () => void) {
        return supabase.channel(`contacts:${myId}`)
            .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'contacts', filter: `user_id=eq.${myId}` }, callback)
            .subscribe();
    },

    // NEW: Real-time location tracking for squad
    subscribeToSquadLocations(callback: (payload: any) => void) {
        return supabase.channel(`squad-locs-${Date.now()}`)
            .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'profiles' }, callback)
            .subscribe();
    },

    // NEW: Get all registered users with valid locations (for map display)
    async getAllNearbyUsers(excludeMyId?: string, userLocation?: LatLng): Promise<Contact[]> {
        const { data: profiles, error } = await supabase
            .from('profiles')
            .select('*')
            .neq('location_lat', 0)  // Exclude users with no location
            .neq('location_lng', 0)
            .order('updated_at', { ascending: false })
            .limit(50); // Limit to 50 nearest for performance

        if (error || !profiles) return [];

        // Filter out current user if specified
        const filtered = excludeMyId
            ? profiles.filter(p => p.id !== excludeMyId)
            : profiles;

        if (filtered.length === 0) {
            // SIMULATION FALLBACK: If no real users, return Ghost Operatives for Demo
            // Use userLocation if available to spawn ghosts nearby, otherwise fallback to Morocco
            const center = userLocation || { lat: 35.6180, lng: -5.2680 };

            return [
                {
                    id: 'sim-1',
                    name: 'Ghost Leader',
                    role: 'Commander',
                    status: SafetyStatus.SAFE,
                    battery: 98,
                    locationName: 'Sector Alpha',
                    coordinates: generateNearbyCoordinates(center, 0.5), // 0.5km away
                    lastSeen: 'Just now',
                    avatarUrl: 'https://api.dicebear.com/9.x/avataaars/svg?seed=ghost',
                    isTrackable: true,
                    lastEmoji: '📡'
                },
                {
                    id: 'sim-2',
                    name: 'Raven Scout',
                    role: 'Scout',
                    status: SafetyStatus.WARNING,
                    battery: 45,
                    locationName: 'Sector Bravo',
                    coordinates: generateNearbyCoordinates(center, 0.8),
                    lastSeen: '2m ago',
                    avatarUrl: 'https://api.dicebear.com/9.x/avataaars/svg?seed=raven',
                    isTrackable: true
                },
                {
                    id: 'sim-3',
                    name: 'Echo Unit',
                    role: 'Medic',
                    status: SafetyStatus.SAFE,
                    battery: 88,
                    locationName: 'Sector Charlie',
                    coordinates: generateNearbyCoordinates(center, 1.2),
                    lastSeen: '5m ago',
                    avatarUrl: 'https://api.dicebear.com/9.x/avataaars/svg?seed=echo',
                    isTrackable: true
                }
            ];
        }

        return filtered.map(mapDBToContact);
    },

    // NEW: Broadcast my location for nearby discovery (when user opens "Nearby" scan)
    async broadcastPresence(userId: string, lat: number, lng: number): Promise<boolean> {
        const { error } = await supabase.from('profiles').update({
            location_lat: lat,
            location_lng: lng,
            updated_at: new Date().toISOString(),
            is_scanning: true
        }).eq('id', userId);
        return !error;
    },

    // NEW: Stop broadcasting presence
    async stopBroadcast(userId: string): Promise<boolean> {
        const { error } = await supabase.from('profiles').update({
            is_scanning: false
        }).eq('id', userId);
        return !error;
    },

    // NEW: Find nearby users who are also scanning (mutual discovery)
    async findNearbyScanning(myId: string, myLat: number, myLng: number, radiusKm: number = 1): Promise<Contact[]> {
        const { data: profiles, error } = await supabase
            .from('profiles')
            .select('*')
            .neq('id', myId)
            .neq('location_lat', 0)
            .neq('location_lng', 0)
            .order('updated_at', { ascending: false })
            .limit(50);

        if (error || !profiles) return [];

        // Calculate distance and filter by radius
        const nearbyWithDistance = profiles
            .map(p => {
                const distVal = this.calculateDistance(myLat, myLng, p.location_lat, p.location_lng);
                return { ...mapDBToContact(p), distance: `${distVal.toFixed(1)}km`, _distVal: distVal };
            })
            .filter(p => p._distVal <= radiusKm)
            .sort((a, b) => a._distVal - b._distVal)
            .map(({ _distVal, ...c }) => c);

        return nearbyWithDistance;
    },

    // Helper: Calculate distance between two points (Haversine formula)
    calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
        const R = 6371; // Earth radius in km
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLng = (lng2 - lng1) * Math.PI / 180;
        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLng / 2) * Math.sin(dLng / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    },

    // NEW: Subscribe to nearby users updates (real-time)
    subscribeToNearby(callback: (payload: any) => void) {
        return supabase.channel(`nearby-${Date.now()}`)
            .on('postgres_changes', {
                event: 'UPDATE',
                schema: 'public',
                table: 'profiles'
            }, callback)
            .subscribe();
    },

    async removeContact(myId: string, contactId: string): Promise<boolean> {
        // 1. Delete forward link
        const { error: e1 } = await supabase.from('contacts').delete().match({ user_id: myId, contact_id: contactId });
        // 2. Delete reverse link (so they don't see me anymore either)
        const { error: e2 } = await supabase.from('contacts').delete().match({ user_id: contactId, contact_id: myId });

        // 3. Delete any contact requests to allow re-adding
        await supabase.from('contact_requests')
            .delete()
            .or(`and(sender_id.eq.${myId},receiver_id.eq.${contactId}),and(sender_id.eq.${contactId},receiver_id.eq.${myId})`);

        return !e1 && !e2;
    },

    // ===========================
    // AUTHENTICATION
    // ===========================

    async registerUser(name: string, username: string, password: string, avatarUrl: string): Promise<{ success: boolean; error?: string; user?: Contact }> {
        try {
            const cleanUser = username.trim().toLowerCase();
            if (cleanUser.length < 3) return { success: false, error: "Username too short" };

            const { data: existing } = await supabase.from('profiles').select('id').eq('username', cleanUser).single();
            if (existing) return { success: false, error: "Username already taken" };

            const newId = crypto.randomUUID();
            const { error: insertError } = await supabase.from('profiles').insert([{
                id: newId, full_name: name, username: cleanUser, password_hash: password, status: 'SAFE',
                location_lat: 0, location_lng: 0, avatar_url: avatarUrl
            }]);
            if (insertError) return { success: false, error: insertError.message };

            return {
                success: true,
                user: {
                    id: newId, name, role: 'Operative', status: SafetyStatus.SAFE, battery: 100,
                    locationName: 'Unknown Sector', coordinates: { lat: 0, lng: 0 },
                    lastSeen: new Date().toLocaleTimeString(), avatarUrl, isTrackable: true
                }
            };
        } catch (e: any) {
            return { success: false, error: e.message };
        }
    },

    async loginUser(username: string, password: string): Promise<{ success: boolean; error?: string; user?: Contact }> {
        const cleanUser = username.trim().toLowerCase();

        if (cleanUser === 'investor' && password === 'naja2026') {
            return {
                success: true,
                user: {
                    id: '00000000-0000-0000-0000-000000000000', name: 'Investor Review', role: 'VIP Observer',
                    status: SafetyStatus.SAFE, battery: 100, locationName: 'HQ - CLASSIFIED',
                    coordinates: { lat: 0, lng: 0 }, lastSeen: new Date().toLocaleTimeString(),
                    avatarUrl: 'https://api.dicebear.com/9.x/avataaars/svg?seed=investor', isTrackable: false
                }
            };
        }

        const { data, error } = await supabase.from('profiles').select('*').eq('username', cleanUser).eq('password_hash', password).single();
        if (error || !data) return { success: false, error: "Invalid username or passcode" };
        return { success: true, user: mapDBToContact(data) };
    },

    async logout(): Promise<void> {
        await supabase.auth.signOut();
        localStorage.removeItem('squad_id');
    },

    // ===========================
    // MESSAGES / CHAT
    // ===========================

    async sendMessage(senderId: string, receiverId: string, content: string) {
        return supabase.from('messages').insert([{ sender_id: senderId, receiver_id: receiverId, content }]);
    },

    async getMessageHistory(myId: string, contactId: string) {
        // RLS SAFETY: We split the query to ensure we get both sides even if complex OR logic is blocked
        const { data: sent, error: e1 } = await supabase.from('messages')
            .select('*')
            .eq('sender_id', myId)
            .eq('receiver_id', contactId)
            .order('created_at', { ascending: false })
            .limit(50);

        const { data: received, error: e2 } = await supabase.from('messages')
            .select('*')
            .eq('sender_id', contactId)
            .eq('receiver_id', myId)
            .order('created_at', { ascending: false })
            .limit(50);

        if (e1 && e2) return { data: [], error: e1 }; // Both failed

        // Combine and Sort
        const combined = [...(sent || []), ...(received || [])].sort((a, b) =>
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );

        return { data: combined, error: null };
    },

    subscribeToMessages(myId: string, callback: (payload: any) => void) {
        console.log('[contactService] Setting up message subscription for:', myId);
        return supabase.channel(`messages-${myId}-${Date.now()}`)
            .on('postgres_changes', {
                event: 'INSERT',
                schema: 'public',
                table: 'messages',
                filter: `receiver_id=eq.${myId}` // FIX: Explicit filter ensures we get our messages even if global RLS is strict
            }, (payload) => {
                console.log('[contactService] Message INSERT received:', payload.new?.content, 'to:', payload.new?.receiver_id);
                // Filter in callback - only pass messages for this user
                if (payload.new && (payload.new.receiver_id === myId || payload.new.sender_id === myId)) {
                    callback(payload);
                }
            })
            .subscribe((status) => {
                console.log('[contactService] Subscription status:', status);
            });
    },

    // NEW: Get list of recent conversations (Inbox)
    async getRecentConversations(myId: string): Promise<any[]> {
        // Fetch all messages involving me
        const { data: messages, error } = await supabase
            .from('messages')
            .select(`
                *,
                sender:profiles!messages_sender_id_fkey(full_name, avatar_url),
                receiver:profiles!messages_receiver_id_fkey(full_name, avatar_url)
            `)
            .or(`sender_id.eq.${myId},receiver_id.eq.${myId}`)
            .order('created_at', { ascending: false });

        if (error || !messages) return [];

        // Group by conversation partner
        const conversations = new Map();
        messages.forEach((msg: any) => {
            const partnerId = msg.sender_id === myId ? msg.receiver_id : msg.sender_id;
            if (!conversations.has(partnerId)) {
                const partnerName = msg.sender_id === myId ? msg.receiver.full_name : msg.sender.full_name;
                const partnerAvatar = msg.sender_id === myId ? msg.receiver.avatar_url : msg.sender.avatar_url;

                conversations.set(partnerId, {
                    contactId: partnerId,
                    contactName: partnerName || 'Unknown',
                    contactAvatar: partnerAvatar || '',
                    lastMessage: msg.content,
                    time: new Date(msg.created_at),
                    unread: false // TODO: Implement read receipts
                });
            }
        });

        return Array.from(conversations.values());
    },

    // NEW: Find User by NID (First 6 chars of UUID)
    // Note: This is an inefficient scan but acceptable for prototype. 
    // Ideally, we'd have a real NID column in DB.
    async findUserByNid(nid: string): Promise<Contact | null> {
        // Fetch all profiles to find match (LIMITATION: won't scale, need DB column)
        const { data: profiles } = await supabase.from('profiles').select('*');
        if (!profiles) return null;

        const match = profiles.find(p => p.id.substring(0, 6).toUpperCase() === nid.toUpperCase());
        return match ? mapDBToContact(match) : null;
    },

    // ===========================
    // HAZARDS
    // ===========================

    async sendAlert(hazard: any) {
        return supabase.from('hazards').insert([hazard]);
    },

    async getActiveHazards() {
        const { data, error } = await supabase.from('hazards').select('*').eq('is_active', true).order('created_at', { ascending: false });
        return { data: data || [], error };
    },

    async getHazardById(hazardId: string) {
        const { data, error } = await supabase.from('hazards').select('*').eq('id', hazardId).single();
        return { data, error };
    },

    subscribeToHazards(callback: (payload: any) => void) {
        return supabase.channel('public:hazards')
            .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'hazards' }, callback)
            .subscribe();
    },

    // ===========================
    // PULSE HANDSHAKE
    // ===========================

    async sendPulseRequest(senderId: string, receiverId: string) {
        await supabase.from('pulse_requests').insert([{ sender_id: senderId, receiver_id: receiverId, status: 'pending' }]);
        return supabase.from('messages').insert([{ sender_id: senderId, receiver_id: receiverId, content: '::PULSE_REQ::' }]);
    },

    async sendPulseResponse(senderId: string, receiverId: string, status: string) {
        await this.updateStatus(senderId, 0, 0, status as SafetyStatus);
        return supabase.from('messages').insert([{ sender_id: senderId, receiver_id: receiverId, content: `::PULSE_ACK::${status}` }]);
    },

    async sendPulseAck(senderId: string, receiverId: string, status: string) {
        return this.sendPulseResponse(senderId, receiverId, status);
    },

    subscribeToPulse(callback: (payload: any) => void) {
        return supabase.channel('public:profiles')
            .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'profiles' }, callback)
            .subscribe();
    },

    // ===========================
    // PROFILE DETAILS & ACHIEVEMENTS
    // ===========================

    async getContactDetails(userId: string): Promise<any | null> {
        const { data, error } = await supabase.from('profiles').select('*').eq('id', userId).single();
        if (error) return null;
        return data;
    },

    async getUserAchievements(userId: string): Promise<string[]> {
        const { data, error } = await supabase.from('user_achievements').select('achievement_id').eq('user_id', userId);
        if (error) return [];
        return data?.map(a => a.achievement_id) || [];
    },

    async incrementReportCount(userId: string): Promise<void> {
        const { data: profile } = await supabase.from('profiles').select('reports_count').eq('id', userId).single();
        const newCount = (profile?.reports_count || 0) + 1;
        await supabase.from('profiles').update({ reports_count: newCount }).eq('id', userId);
        await this.checkAndAwardBadges(userId, 'report', newCount);
    },

    async checkAndAwardBadges(userId: string, type: 'report' | 'contact' | 'message', count: number): Promise<void> {
        const BADGE_THRESHOLDS: Record<string, { type: string; threshold: number }[]> = {
            'first_alert': [{ type: 'report', threshold: 1 }],
            'watchdog': [{ type: 'report', threshold: 5 }],
            'guardian': [{ type: 'report', threshold: 10 }],
            'connector': [{ type: 'contact', threshold: 5 }],
            'communicator': [{ type: 'message', threshold: 20 }],
        };

        for (const [badgeId, requirements] of Object.entries(BADGE_THRESHOLDS)) {
            const req = requirements.find(r => r.type === type);
            if (req && count >= req.threshold) {
                const { data: existing } = await supabase.from('user_achievements').select('id').eq('user_id', userId).eq('achievement_id', badgeId).single();
                if (!existing) {
                    await supabase.from('user_achievements').insert([{ user_id: userId, achievement_id: badgeId }]);
                    console.log(`🏆 Badge awarded: ${badgeId}`);
                }
            }
        }
    }
};

const mapDBToContact = (db: DBProfile): Contact => ({
    id: db.id,
    name: db.full_name || 'Unknown Agent',
    role: 'Operative',
    status: (db.status as SafetyStatus) || SafetyStatus.SAFE,
    battery: 100,
    locationName: db.location_name || 'Unknown Sector',
    coordinates: { lat: db.location_lat || 0, lng: db.location_lng || 0 },
    lastSeen: db.updated_at ? new Date(db.updated_at).toLocaleTimeString() : 'Unknown',
    avatarUrl: db.avatar_url || '',
    isTrackable: true,
    nid: db.id.substring(0, 6).toUpperCase()
});
// Helper for standardized avatar display
export const getEffectiveAvatar = (contact: Contact | null | undefined): string => {
    if (!contact) return 'https://api.dicebear.com/9.x/avataaars/svg?seed=NajaAgent';
    if (contact.avatarUrl && contact.avatarUrl.length > 5) return contact.avatarUrl;
    // Fallback: Use ID as stable seed
    return `https://api.dicebear.com/9.x/avataaars/svg?seed=${contact.id}`;
};
